import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Search, Eye, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = '/api';

export const AdminTalentPoolPage = () => {
  const { t } = useLanguage();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      const response = await fetch(`${API}/admin/talent-pool`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCandidates(data);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to load talent pool');
    } finally {
      setLoading(false);
    }
  };

  const filteredCandidates = candidates.filter(cand => 
    cand.full_name.toLowerCase().includes(search.toLowerCase()) ||
    cand.email.toLowerCase().includes(search.toLowerCase()) ||
    cand.skills?.some(s => s.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="p-6" data-testid="admin-talent-pool-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#111827]">{t('admin.talentPool')}</h1>
        <p className="text-[#6B7280]">Browse candidate profiles</p>
      </div>

      <Card>
        <CardHeader>
          <div className="relative max-w-md">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
            <Input
              placeholder="Search by name, email, or skills..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ps-9"
              data-testid="search-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD] mx-auto"></div>
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div className="text-center py-8 text-[#6B7280]">{t('admin.noData')}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Skills</TableHead>
                    <TableHead>Experience</TableHead>
                    <TableHead>{t('admin.date')}</TableHead>
                    <TableHead className="text-end">{t('admin.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCandidates.map((cand, index) => (
                    <TableRow key={cand.candidate_id} data-testid={`candidate-row-${index}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-[#111827]">{cand.full_name}</p>
                          <p className="text-sm text-[#6B7280]">{cand.current_position}</p>
                        </div>
                      </TableCell>
                      <TableCell>{cand.city}, {cand.country}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {cand.skills?.slice(0, 3).map((skill, i) => (
                            <Badge key={skill} variant="secondary" className="text-xs">{skill}</Badge>
                          ))}
                          {cand.skills?.length > 3 && (
                            <Badge variant="secondary" className="text-xs">+{cand.skills.length - 3}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{cand.experience_years ? `${cand.experience_years} years` : '-'}</TableCell>
                      <TableCell className="text-sm text-[#6B7280]">
                        {cand.created_at ? format(new Date(cand.created_at), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell className="text-end">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedCandidate(cand)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Candidate Details Dialog */}
      <Dialog open={!!selectedCandidate} onOpenChange={() => setSelectedCandidate(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Candidate Profile</DialogTitle>
          </DialogHeader>
          {selectedCandidate && (
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#6B7280]">Full Name</p>
                  <p className="font-medium">{selectedCandidate.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Email</p>
                  <p className="font-medium">{selectedCandidate.email}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Phone</p>
                  <p className="font-medium">{selectedCandidate.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Location</p>
                  <p className="font-medium">{selectedCandidate.city}, {selectedCandidate.country}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Current Position</p>
                  <p className="font-medium">{selectedCandidate.current_position || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Current Company</p>
                  <p className="font-medium">{selectedCandidate.current_company || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Experience</p>
                  <p className="font-medium">{selectedCandidate.experience_years ? `${selectedCandidate.experience_years} years` : '-'}</p>
                </div>
                {selectedCandidate.desired_salary && (
                  <div>
                    <p className="text-sm text-[#6B7280]">Desired Salary</p>
                    <p className="font-medium">{selectedCandidate.currency} {selectedCandidate.desired_salary?.toLocaleString()}</p>
                  </div>
                )}
              </div>

              {selectedCandidate.desired_positions?.length > 0 && (
                <div>
                  <p className="text-sm text-[#6B7280] mb-2">Desired Positions</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedCandidate.desired_positions.map((pos, i) => (
                      <Badge key={pos} variant="outline">{pos}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedCandidate.skills?.length > 0 && (
                <div>
                  <p className="text-sm text-[#6B7280] mb-2">Skills</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedCandidate.skills.map((skill, i) => (
                      <Badge key={skill} variant="secondary">{skill}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedCandidate.languages?.length > 0 && (
                <div>
                  <p className="text-sm text-[#6B7280] mb-2">Languages</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedCandidate.languages.map((lang, i) => (
                      <Badge key={lang} variant="outline">{lang}</Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-4">
                {selectedCandidate.linkedin && (
                  <a href={selectedCandidate.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#33AADD] hover:underline">
                    <ExternalLink className="h-4 w-4" /> LinkedIn
                  </a>
                )}
                {selectedCandidate.cv_url && (
                  <a href={selectedCandidate.cv_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#33AADD] hover:underline">
                    <ExternalLink className="h-4 w-4" /> CV
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTalentPoolPage;
